import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidFavorites — the kid's "My Book". One source of truth for
// hearted canonicals + recipes. Family-adults can read (so a
// settings screen can show "Peezy's saved meals"); only the kid
// can insert / delete their own rows.
//
// Returns:
//   favorites   — array of { id, item_kind, item_id, item_name, item_emoji, created_at }
//   isFavorite(kind, id)
//   add({ kind, id, name, emoji })
//   remove({ kind, id })   // by composite key, not row id
//
// No localStorage cache. Realtime keeps it fresh; the kid app
// mounts a single list at a time so the boot cost is trivial.

export function useKidFavorites(kidUserId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidUserId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("kid_favorites")
      .select("id, item_kind, item_id, item_emoji, item_name, created_at")
      .eq("kid_user_id", kidUserId)
      .order("created_at", { ascending: false });
    if (error) {
      setError(error);
      console.error("[kid-favorites] load:", error);
    }
    setRows(data || []);
    setLoading(false);
  }, [kidUserId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:kid_favorites:${kidUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_favorites", filter: `kid_user_id=eq.${kidUserId}` },
        () => load())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, load]);

  const isFavorite = useCallback(
    (kind, id) => rows.some((r) => r.item_kind === kind && r.item_id === id),
    [rows],
  );

  const add = useCallback(async ({ kind, id, name, emoji }) => {
    if (!kidUserId || !kind || !id) return;
    const { error } = await supabase.from("kid_favorites").insert({
      kid_user_id: kidUserId,
      item_kind:   kind,
      item_id:     id,
      item_emoji:  emoji || null,
      item_name:   name || id,
    });
    if (error && !String(error.message || "").includes("duplicate key")) {
      console.warn("[kid-favorites] add:", error.message);
      throw error;
    }
  }, [kidUserId]);

  const remove = useCallback(async ({ kind, id }) => {
    if (!kidUserId || !kind || !id) return;
    const { error } = await supabase
      .from("kid_favorites")
      .delete()
      .eq("kid_user_id", kidUserId)
      .eq("item_kind", kind)
      .eq("item_id", id);
    if (error) {
      console.warn("[kid-favorites] remove:", error.message);
      throw error;
    }
  }, [kidUserId]);

  const toggle = useCallback(async ({ kind, id, name, emoji }) => {
    if (isFavorite(kind, id)) await remove({ kind, id });
    else await add({ kind, id, name, emoji });
  }, [isFavorite, add, remove]);

  return {
    favorites: rows,
    loading,
    error,
    isFavorite,
    add,
    remove,
    toggle,
    refresh: load,
  };
}
